import { useEffect, useState } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
// @ts-ignore — react-js-pagination has no bundled types (see PaginationControls)
import Pagination from "react-js-pagination";

import AppRoutes from "../../routes/AppRoutes";
import ManagerDashboardPersonDto from "../../types/dtos/dashboards/ManagerDashboardPersonDto";
import ManagerDashboardUpdateDto from "../../types/dtos/dashboards/ManagerDashboardUpdateDto";
import ManagerDashboardUpdateSectionDisplayType from "../../types/dtos/dashboards/ManagerDashboardUpdateSectionDisplayType";
import { AppraisalLevelClientTaskTypeDto } from "../../types/dtos/users/AppraisalLevelClientTaskTypeDto";
import IndividualUpdate from "./IndividualUpdate";
import { Badge } from "../common";
import SuccessAlert from "../alerts/SuccessAlert";
import DangerAlert from "../alerts/DangerAlert";
import { AdvancedTasksUpdateNotification } from "./AdvancedTasksUpdateNotification";
import ManagerDashboardEmptyState from "./ManagerDashboardEmptyState";
import {
  isManagerTodo,
  managerUpdateUsesCollabDocLink,
} from "./shared/managerTodoHelper";
import useManagerTodoActions from "./shared/useManagerTodoActions";

interface UpdatesWidgetProps {
  users: ManagerDashboardPersonDto[];
  managerUpdates: ManagerDashboardUpdateDto[];
  isLoading: boolean;
  taskTypes: AppraisalLevelClientTaskTypeDto[];
  reloadUpdates: () => void;
}

const ITEMS_PER_PAGE = 5;
const LOADING_ROW_COUNT = 3;

const UPDATE_ROW_CLASS =
  "mb-1 flex flex-row gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 last:mb-0 hover:outline hover:outline-gray-400";

interface UpdatesWidgetTabHeaderProps {
  displayType: ManagerDashboardUpdateSectionDisplayType;
  filteredToDosCount: number;
  filteredUpdatesCount: number;
  isInteractive: boolean;
  showSort: boolean;
  sortUpdatesByNewest: boolean;
  onSelectToDos: () => void;
  onSelectUpdates: () => void;
  onToggleSort: () => void;
  toDosLabel: string;
  updatesLabel: string;
  sortingByLabel: string;
  newestLabel: string;
  oldestLabel: string;
}

function UpdatesWidgetTabHeader({
  displayType,
  filteredToDosCount,
  filteredUpdatesCount,
  isInteractive,
  showSort,
  sortUpdatesByNewest,
  onSelectToDos,
  onSelectUpdates,
  onToggleSort,
  toDosLabel,
  updatesLabel,
  sortingByLabel,
  newestLabel,
  oldestLabel,
}: UpdatesWidgetTabHeaderProps) {
  const getTabClassNames = (isActive: boolean) =>
    cx(
      "group inline-flex items-center gap-2 rounded-sm px-1 py-1 !mb-1 mr-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
      isActive
        ? "text-primary hover:cursor-default"
        : "text-gray-500 hover:cursor-pointer hover:text-gray-700",
      !isInteractive && "pointer-events-none",
    );

  const getTitleClassNames = (isActive: boolean) =>
    cx("text-xl", isActive ? "font-bold" : "font-semibold");

  return (
    <div className="flex flex-col md:flex-row">
      <div
        role="tablist"
        aria-label={updatesLabel}
        className="grow"
      >
        <button
          type="button"
          role="tab"
          aria-selected={displayType === "TO-DOS"}
          disabled={!isInteractive}
          className={getTabClassNames(displayType === "TO-DOS")}
          onClick={onSelectToDos}
        >
          <span className={getTitleClassNames(displayType === "TO-DOS")}>
            {toDosLabel}
          </span>
          <TabHeaderCountBadge
            count={filteredToDosCount}
            isActive={displayType === "TO-DOS"}
          />
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={displayType === "UPDATES"}
          disabled={!isInteractive}
          className={getTabClassNames(displayType === "UPDATES")}
          onClick={onSelectUpdates}
        >
          <span className={getTitleClassNames(displayType === "UPDATES")}>
            {updatesLabel}
          </span>
          <TabHeaderCountBadge
            count={filteredUpdatesCount}
            isActive={displayType === "UPDATES"}
          />
        </button>
      </div>
      {showSort && (
        <div className="shrink p-2 text-right">
          <p className="text-xs text-gray-600" aria-live="polite">
            {sortingByLabel}
            <button
              type="button"
              className="primary-colour inline-block cursor-pointer pl-1 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 rounded-sm"
              onClick={onToggleSort}
            >
              {sortUpdatesByNewest ? newestLabel : oldestLabel}
            </button>
          </p>
        </div>
      )}
    </div>
  );
}

function TabHeaderCountBadge({
  count,
  isActive,
}: {
  count: number;
  isActive: boolean;
}) {
  return (
    <Badge
      text={count.toString()}
      backgroundColourClassName={cx(
        "hidden md:inline-block",
        isActive ? "accent-bg" : "bg-slate-400 group-hover:bg-slate-600",
      )}
      textColourClassName="text-white"
      marginClassName=""
      classNames="!py-0"
      textSize="text-base"
    />
  );
}

function UpdatesWidgetLoadingRow() {
  return (
    <div className={UPDATE_ROW_CLASS}>
      <div className="flex min-w-0 flex-grow animate-pulse flex-col gap-3 lg:flex-row lg:items-center">
        <div className="h-6 w-20 shrink-0 rounded bg-gray-200/80" />
        <div className="flex min-w-0 flex-grow flex-col gap-2">
          <div className="h-4 w-full max-w-lg rounded bg-gray-200/80" />
          <div className="h-4 w-3/4 max-w-md rounded bg-gray-200/60" />
        </div>
        <div className="hidden h-8 w-28 shrink-0 rounded bg-gray-200/80 lg:block" />
      </div>
    </div>
  );
}

function UpdatesWidgetLoadingState({ loadingLabel }: { loadingLabel: string }) {
  return (
    <div
      className="mt-2 flex flex-col"
      aria-busy="true"
      aria-label={loadingLabel}
    >
      <span className="sr-only">{loadingLabel}</span>
      {Array.from({ length: LOADING_ROW_COUNT }, (_, index) => (
        <UpdatesWidgetLoadingRow key={`updates-loading-${index}`} />
      ))}
    </div>
  );
}

function UpdatesWidgetEmptyState() {
  const { t } = useTranslation();

  return (
    <div className="mt-2 rounded-md border border-gray-200 bg-white px-4 py-6 text-center">
      <FontAwesomeIcon
        icon={faCircleCheck}
        className="primary-colour mb-3"
        size="2x"
        aria-hidden="true"
      />
      <p className="text-sm font-semibold text-gray-700">
        {t(
          "ManagerDashboard.Updates.EmptyTitle",
          "You're all caught up",
        )}
      </p>
      <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
        {t(
          "ManagerDashboard.Updates.NoUpdatesForPeople",
          "There are no updates for any of your people at the moment",
        )}
      </p>
    </div>
  );
}

function UpdatesWidget({
  users,
  isLoading,
  managerUpdates,
  taskTypes,
  reloadUpdates,
}: UpdatesWidgetProps) {
  const { t } = useTranslation();

  const [activePageNumber, setActivePageNumber] = useState<number>(1);
  const [displayType, setDisplayType] =
    useState<ManagerDashboardUpdateSectionDisplayType>("TO-DOS");
  const [displayItems, setDisplayItems] = useState<ManagerDashboardUpdateDto[]>(
    [],
  );
  const [filteredToDos, setFilteredToDos] = useState<
    ManagerDashboardUpdateDto[]
  >([]);
  const [filteredToDosCount, setFilteredToDosCount] = useState<number>(0);
  const [filteredUpdates, setFilteredUpdates] = useState<
    ManagerDashboardUpdateDto[]
  >([]);
  const [filteredUpdatesCount, setFilteredUpdatesCount] = useState<number>(0);
  const [sortUpdatesByNewest, setSortUpdatesByNewest] = useState<boolean>(true);

  const {
    handleSkipConfirmationModalClick,
    handleLoadDualPrepCollabDoc,
    skipPlanningPopUp,
    showSuccessNotification,
    successNotificationMessage,
    showErrorNotification,
    errorNotificationMessage,
  } = useManagerTodoActions(users, reloadUpdates);

  const toDosLabel = t("ManagerDashboard.Updates.ToDos", "To-do");
  const updatesLabel = t("ManagerDashboard.Updates.Updates", "Updates");
  const sortingByLabel = t("ManagerDashboard.Updates.SortingBy", "Sorting by:");
  const newestLabel = t("ManagerDashboard.Updates.Newest", "Newest");
  const oldestLabel = t("ManagerDashboard.Updates.Oldest", "Oldest");
  const loadingLabel = t("Common.Loading", "Loading");

  useEffect(() => {
    const filtered = filterItems(managerUpdates);
    updateFilteredStates(filtered);
  }, [managerUpdates]);

  useEffect(() => {
    const filtered = filterItems(managerUpdates);
    updateFilteredStates(filtered);
  }, [displayType, sortUpdatesByNewest]);

  const updateFilteredStates = (
    filteredManagerUpdates: ManagerDashboardUpdateDto[],
  ) => {
    const toDos = filteredManagerUpdates.filter((u) => isManagerTodo(u));
    const updates = filteredManagerUpdates.filter((u) => !isManagerTodo(u));

    setFilteredToDos(toDos);
    setFilteredToDosCount(toDos.length);
    setFilteredUpdates(updates);
    setFilteredUpdatesCount(updates.length);

    const nextDisplayItems = displayType === "TO-DOS" ? toDos : updates;
    handlePaginationChange(1, nextDisplayItems);
  };

  const handlePaginationChange = (
    pageNumber: number,
    latestUpdates?: ManagerDashboardUpdateDto[],
  ) => {
    const startIndex = (pageNumber - 1) * ITEMS_PER_PAGE;
    let sourceItems: ManagerDashboardUpdateDto[];

    if (latestUpdates !== undefined) {
      sourceItems = latestUpdates;
    } else {
      sourceItems =
        displayType === "TO-DOS"
          ? filterItems(filteredToDos)
          : filterItems(filteredUpdates);
    }

    setDisplayItems(
      sourceItems.slice(startIndex, startIndex + ITEMS_PER_PAGE),
    );
    setActivePageNumber(pageNumber);
  };

  const filterItems = (unfilteredUpdates: ManagerDashboardUpdateDto[]) => {
    const filtered = [...unfilteredUpdates];

    return sortUpdatesByNewest
      ? filtered.sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        )
      : filtered.sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
        );
  };

  const activeTabTotalCount =
    displayType === "TO-DOS" ? filteredToDosCount : filteredUpdatesCount;
  const showPagination = !isLoading && activeTabTotalCount > ITEMS_PER_PAGE;

  return (
    <>
      <div
        id="activity-box"
        className="rounded-lg p-4 background-tint-bg"
      >
        <UpdatesWidgetTabHeader
          displayType={displayType}
          filteredToDosCount={filteredToDosCount}
          filteredUpdatesCount={filteredUpdatesCount}
          isInteractive={!isLoading}
          showSort={!isLoading && displayItems.length > 0}
          sortUpdatesByNewest={sortUpdatesByNewest}
          onSelectToDos={() => setDisplayType("TO-DOS")}
          onSelectUpdates={() => setDisplayType("UPDATES")}
          onToggleSort={() => setSortUpdatesByNewest((prev) => !prev)}
          toDosLabel={toDosLabel}
          updatesLabel={updatesLabel}
          sortingByLabel={sortingByLabel}
          newestLabel={newestLabel}
          oldestLabel={oldestLabel}
        />

        <div>
          <hr className="border-gray-200" />
          <div className="mb-3 mt-2">
            {isLoading && <UpdatesWidgetLoadingState loadingLabel={loadingLabel} />}

            {!isLoading && managerUpdates.length === 0 && (
              <UpdatesWidgetEmptyState />
            )}

            {!isLoading &&
              managerUpdates.length > 0 &&
              displayItems.length === 0 && (
                <ManagerDashboardEmptyState
                  optionalText={t(
                    "ManagerDashboard.Updates.NoResultsHint",
                    'If you are looking for a completed Journey, click the "Actions" button on the relevant person below and select "Journey History"',
                  )}
                />
              )}

            {!isLoading && displayItems.length > 0 && (
              <div className="mt-2 text-sm primary-colour">
                {displayItems.map((update, index) => (
                  <div
                    key={`${update.updateType}_${update.userId}_${update.clientFormTitle}_${index}`}
                    className={UPDATE_ROW_CLASS}
                  >
                    <div className="group min-w-0 flex-grow">
                      {update.updateType ===
                      "ADVANCED-TASKS-UPDATED-BY-EMPLOYEE" ? (
                        <AdvancedTasksUpdateNotification
                          update={update}
                          users={users}
                          taskTypes={taskTypes}
                        />
                      ) : managerUpdateUsesCollabDocLink(update) ? (
                        <Link
                          to={AppRoutes.collaborativeDocument.generateRoute(
                            update.answerSetUniqueId!,
                            false,
                            null,
                          )}
                          className="flex cursor-pointer flex-row"
                        >
                          <IndividualUpdate users={users} update={update} />
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="flex w-full cursor-pointer flex-row border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 rounded-sm"
                          onClick={() =>
                            handleLoadDualPrepCollabDoc(
                              update.userId,
                              update.journeyReference!,
                            )
                          }
                        >
                          <IndividualUpdate users={users} update={update} />
                        </button>
                      )}
                    </div>
                    {update.updateType === "DUAL-PREP-PLANNING" &&
                      update.canBeSkipped && (
                        <button
                          type="button"
                          className="btn-secondary shrink-0 self-center bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                          onClick={() =>
                            handleSkipConfirmationModalClick(update)
                          }
                        >
                          {t(
                            "DashboardRightBar.SkipPlanning",
                            "Skip planning",
                          )}
                        </button>
                      )}
                  </div>
                ))}
              </div>
            )}

            {showSuccessNotification && (
              <SuccessAlert
                prefix={t("Common.Success")}
                message={t(successNotificationMessage)}
                includeMarginTop={true}
              />
            )}
            {showErrorNotification && (
              <DangerAlert
                prefix={t("Common.Validation.Oops")}
                message={t(errorNotificationMessage)}
              />
            )}
          </div>

          {showPagination && (
            <div className="mb-2 text-right text-xs">
              <Pagination
                activePage={activePageNumber}
                itemsCountPerPage={ITEMS_PER_PAGE}
                totalItemsCount={activeTabTotalCount}
                pageRangeDisplayed={5}
                onChange={handlePaginationChange}
                activeClass="pagination-active-list-item"
                itemClass="pagination-list-item"
                itemClassFirst="pagination-first-item"
                itemClassLast="pagination-last-item"
              />
            </div>
          )}
        </div>
      </div>
      {skipPlanningPopUp}
    </>
  );
}

export default UpdatesWidget;
